import { useMutation, useQuery } from "react-query";
import { client } from "./axios";
import { config } from "../config";
import {
  OrganizationInfo,
  Organization,
  QueryOptions,
  ApiError,
  ApiBasePathParams,
  OrganizationProfileInfo,
  Address,
  WorldpayCredentials,
  PaymentReminder,
  Feature,
} from "../types";
import { DEFAULT_QUERY_OPTIONS } from "../constants";
import { AxiosError, AxiosResponse } from "axios";
import { Features } from "../enums";

export const useFetchOrganizationsQuery = (
  queryOptions?: QueryOptions<Organization[]>,
) => {
  const fetchOrganizations = async (): Promise<Organization[]> => {
    const path = config.api.organizationService.getAllOrganizations;
    const response = await client.get<any>(path);

    return response.data.organisations;
  };

  return useQuery("fetchOrganizations", fetchOrganizations, {
    ...queryOptions,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useFetchOrganizationInfoQuery = (
  orgId: number,
  queryOptions?: QueryOptions<OrganizationInfo>,
) => {
  const fetchOrganizationInfo = async (): Promise<OrganizationInfo> => {
    const path = config.api.organizationService.getOrganisation.replace(
      "{orgId}",
      orgId.toString(),
    );

    const response = await client.get<{
      organisation: OrganizationInfo;
    }>(path);

    return response.data.organisation;
  };

  return useQuery(["fetchOrganizationInfo", orgId], fetchOrganizationInfo, {
    ...queryOptions,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useCreateOrganizationMutation = () => {
  const mutation = useMutation<
    number,
    AxiosError<ApiError>,
    Pick<ApiBasePathParams, "orgId"> & {
      organizationInfo: {
        name: string;
        logo?: string;
        country: string;
      };
    },
    unknown
  >(
    async (
      data: Pick<ApiBasePathParams, "orgId"> & {
        organizationInfo: {
          name: string;
          logo?: string;
        };
      },
    ) => {
      const { orgId, organizationInfo } = data;
      const path = config.api.organizationService.createOrganisation.replace(
        "{orgId}",
        orgId.toString(),
      );

      const response = await client.post<{ orgId: number }>(path, {
        ...organizationInfo,
      });
      return response.data.orgId;
    },
  );

  return mutation;
};

export const useUpdateOrganizationMutation = () => {
  const mutation = useMutation<any, AxiosError<ApiError>, any, any>(
    async (
      data: Pick<ApiBasePathParams, "orgId"> & Partial<OrganizationProfileInfo>,
    ) => {
      const { orgId, ...organizationProfileInfo } = data;
      const path = config.api.organizationService.updateOrganisation.replace(
        "{orgId}",
        orgId.toString(),
      );

      const response = await client.patch(path, { ...organizationProfileInfo });

      return response;
    },
  );

  return mutation;
};

export const useUpdateOrganizationPaymentReminderMutation = () => {
  const mutation = useMutation<any, AxiosError<ApiError>, any, any>(
    async (data: Pick<ApiBasePathParams, "orgId"> & PaymentReminder) => {
      const { orgId, ...paymentReminder } = data;
      const path =
        config.api.organizationService.updateOrganisationPaymentReminder.replace(
          "{orgId}",
          orgId.toString(),
        );

      const response = await client.patch(path, paymentReminder);

      return response;
    },
  );

  return mutation;
};

export const useFetchOrganizationLogoUploadPreSignedUrlQuery = (
  orgId: number,
  fileName: string,
  queryOptions?: QueryOptions<string>,
) => {
  const fetchPreSignedUrl = async (): Promise<string> => {
    const path = config.api.organizationService.getOrgLogoUploadSignedUrl
      .replace("{orgId}", orgId.toString())
      .replace("{fileName}", fileName.toString());

    const response = await client.get<{ url: string; key: string }>(path);

    return response.data.url;
  };

  return useQuery(
    ["organizationFetchPreSignedUrl", orgId, fileName],
    fetchPreSignedUrl,
    {
      ...queryOptions,
      ...DEFAULT_QUERY_OPTIONS,
    },
  );
};

export const useUpdateOrganizationAddressMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    Pick<ApiBasePathParams, "orgId"> & {
      primaryAddress?: Omit<Address, "country">;
      remittanceAddress?: Omit<Address, "country">;
    },
    unknown
  >(
    async (
      data: Pick<ApiBasePathParams, "orgId"> & {
        primaryAddress?: Omit<Address, "country">;
        remittanceAddress?: Omit<Address, "country">;
      },
    ) => {
      const { orgId, ...organizationAddressInfo } = data;
      const path =
        config.api.organizationService.updateOrganisationAddress.replace(
          "{orgId}",
          orgId.toString(),
        );

      const response = await client.patch(path, { ...organizationAddressInfo });

      return response;
    },
  );

  return mutation;
};

export const useDeleteOrganizationMutation = () => {
  const mutation = useMutation(async (data: { orgId: number }) => {
    const { orgId } = data;
    const path = config.api.organizationService.deleteOrganisation.replace(
      "{orgId}",
      orgId.toString(),
    );

    const response = await client.delete(path);
    return response;
  });

  return mutation;
};

export const useFetchWorldpayCredentialsQuery = (
  orgId: number,
  queryOptions?: QueryOptions<string>,
) => {
  const fetchWorldpayCredentials = async (): Promise<WorldpayCredentials> => {
    const path = config.api.organizationService.getWorldpayCredentials.replace(
      "{orgId}",
      orgId.toString(),
    );

    const response = await client.get<any>(path);

    return response.data.credentials;
  };

  return useQuery(
    ["fetchWorldpayCredentials", orgId],
    fetchWorldpayCredentials,
    {
      ...queryOptions,
      ...DEFAULT_QUERY_OPTIONS,
    },
  );
};

export const useUpdateWorldpayCredentialsMutation = () => {
  const mutation = useMutation<any, AxiosError<ApiError>, any, any>(
    async (data: Pick<ApiBasePathParams, "orgId"> & WorldpayCredentials) => {
      const { orgId, ...worldpayCredentials } = data;
      const path =
        config.api.organizationService.createOrUpdateWorldpayCredentials.replace(
          "{orgId}",
          orgId.toString(),
        );

      const response = await client.post(path, { ...worldpayCredentials });

      return response;
    },
  );

  return mutation;
};

export const useFetchOrganizationFeaturesQuery = (
  orgId: number,
  queryOptions?: QueryOptions<Feature[]>,
) => {
  const fetchOrganizationFeatures = async (): Promise<Feature[]> => {
    const path = config.api.organizationService.getOrganizationFeatures.replace(
      "{orgId}",
      orgId.toString(),
    );

    const response = await client.get<{ features: Feature[] }>(path);

    return response.data.features;
  };

  return useQuery(
    ["fetchOrganizationFeatures", orgId],
    fetchOrganizationFeatures,
    {
      ...queryOptions,
      ...DEFAULT_QUERY_OPTIONS,
    },
  );
};
export const useUpdateOrganizationFeatureFlagMutation = () => {
  const mutation = useMutation(
    async (data: { orgId: number; feature: Features; enabled: boolean }) => {
      const { orgId, feature, enabled } = data;

      const path =
        config.api.organizationService.updateOrganizationFeatureFlag.replace(
          "{orgId}",
          orgId.toString(),
        );

      const response = await client.put(path, {
        feature,
        enabled,
      });

      return response.data;
    },
  );

  return mutation;
};
